import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, MapPin, Phone, Mail, ArrowRight } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { BRAND_INFO, CONTACT_INFO, COUNTRY_OF_ORIGIN } from '../data/mock';

const AboutPage = () => {
  usePageTitle(
    `About us | ${BRAND_INFO.name}`,
    `${BRAND_INFO.name} is a bloodwork analysis platform operated by ${CONTACT_INFO.corporateName} — built for athletes and performance-focused individuals.`
  );

  return (
    <>
      <Header />

      <section className="pt-28 lg:pt-36 pb-16 bg-cream-100">
        <div className="container-wide">
          <p className="eyebrow">About us</p>
          <h1 className="mt-5 font-display text-display-lg text-ink-900 max-w-4xl">
            We built {BRAND_INFO.name} because we were tired of{' '}
            <em className="italic text-brick-500">flat PDFs</em>.
          </h1>
          <p className="mt-6 text-lg text-ink-600 max-w-3xl">
            Athletes and performance-focused humans pull more bloodwork than the average
            patient. They also get less out of it. The standard lab PDF flattens dozens of
            interesting trajectories into a stack of one-day snapshots. We rebuilt the experience
            around something obvious in retrospect: every draw is a data point on a timeline.
          </p>
        </div>
      </section>

      <section className="pb-20 bg-cream-100">
        <div className="container-wide grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-6 text-ink-700 text-[16px] leading-relaxed">
            <p>
              Our team comes from a mix of training, software, and clinical-data backgrounds.
              We have spent more time than we care to admit comparing PDF dates with sticky
              notes, exporting CSVs by hand, and trying to spot a thirty-percent change in a
              marker by squinting at two static reports.
            </p>
            <p>
              {BRAND_INFO.name} unifies your draws — whether they came from us or from your
              prior labs — into one searchable, plottable, timeline-aware account. We never
              sell health data, never run health-data ads, and never share your dashboard with
              third parties beyond the partner labs that fulfilled the work.
            </p>
            <p>
              We are based in the United States and operated by{' '}
              <span className="text-ink-900 font-medium">{CONTACT_INFO.corporateName}</span>{' '}
              from Palatine, Illinois. Country of origin: {COUNTRY_OF_ORIGIN}.
            </p>
            <p>
              We are not a medical or diagnostic service. The dashboards we build, the
              educational content we publish, and the suggested ranges we display are intended
              for personal awareness. They do not replace a relationship with a licensed
              healthcare professional. Always discuss meaningful changes with your clinician.
            </p>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-cream-50 border border-cream-200 rounded-3xl p-7">
              <h3 className="font-display text-2xl text-ink-900">Company info</h3>
              <dl className="mt-5 space-y-4 text-[15px]">
                <div className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 mt-1 text-brick-500" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-ink-400">Operated by</div>
                    <div className="text-ink-900">{CONTACT_INFO.corporateName}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-brick-500" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-ink-400">Mailing address</div>
                    <div className="text-ink-800">
                      {CONTACT_INFO.address.street}<br />
                      {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                      {CONTACT_INFO.address.country}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 mt-1 text-brick-500" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-ink-400">Phone</div>
                    <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-ink-900 hover:text-brick-500">
                      {CONTACT_INFO.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-1 text-brick-500" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-ink-400">Email</div>
                    <a href={`mailto:${CONTACT_INFO.email}`} className="text-ink-900 hover:text-brick-500">
                      {CONTACT_INFO.email}
                    </a>
                  </div>
                </div>
              </dl>
              <Link
                to="/panels"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-brick-500 hover:bg-brick-600 text-white font-medium px-5 py-3 rounded-full transition-colors"
              >
                Order labs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AboutPage;
